import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import type { PhotoCandid } from '@/api/types/photo'

/**
 * Looks up a PhotoCandid by candidId from any album photos already cached in React Query.
 * Makes no network request — returns null if the candid isn't in the cache.
 * The right-panel photos tray populates the cache when a user browses albums.
 */
export function usePhotoCandidFromCache(candidId: string | undefined): PhotoCandid | null {
  const queryClient = useQueryClient()

  return useMemo(() => {
    if (!candidId) return null

    const cachedQueries = queryClient.getQueriesData<PhotoCandid[]>({
      queryKey: [...queryKeys.photos.all, 'albumPhotos'],
    })

    for (const [, photos] of cachedQueries) {
      if (!Array.isArray(photos)) continue

      const found = photos.find((p) => p.candidId === candidId)

      if (found) return found
    }

    return null
  }, [candidId, queryClient])
}
